import React, { useEffect, useState } from "react";
import { Form, Button, Steps, message, Grid } from "antd";
import "./AddProducts.scss";
import "../../../styles/main.scss";
import FormInput from "../../../components/FormInputs/FormInput";
import FormSelect from "../../../components/FormInputs/FormSelect";
import FormEditor from "../../../components/FormInputs/FormEditor";
import FormTextarea from "../../FormInputs/FormTextarea";
import ProductUploadWithCrop from "../../FormInputs/ProductUploadInput";
import FormSelectColor from "../../FormInputs/FormSelectColor";
import { getApi, postApi } from "../../../utils/apiServices";
import {
  ADD_OR_UPDATE_PRODUCT_VARIANT,
  GET_CATEGORIES_LIST,
  GET_PRODUCTS_BY_ID,
  GET_PRODUCT_VARIENT_DATA,
  ADD_PRODUCT,
  UPDATE_PRODUCT,
  GET_GST_DROPDOWN,
} from "../../../utils/apiPath";
import FormWeight from "../../FormInputs/FormWeight";
import { IMAGE_BASE_PATH } from "../../../utils/constant";
import FormNumericInput from "../../FormInputs/FormNumberInput";
import { useNavigate, useParams } from "react-router-dom";
import ToastHelper from "../../../services/ToastHelper";
import {
  colorOptions,
  schemaTypes,
  sizeOptions,
} from "../../../services/customData";

const { Step } = Steps;

const AddProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [current, setCurrent] = useState(0);
  const [categoryList1, setCategoryList1] = useState([]);
  const [categoryList2, setCategoryList2] = useState([]);
  const [categoryList3, setCategoryList3] = useState([]);
  const [gstValues, setGstValues] = useState([]);
  const [productId, setProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const initialData = form.getFieldsValue();

  useEffect(() => {
    if (id) {
      getProductData();
    }
  }, [id]);

  const getProductData = async () => {
    try {
      const res = await getApi(`${GET_PRODUCTS_BY_ID}?productId=${id}`);

      if (res?.statusCode === 200) {
        setProductData(res?.data);
        return res?.data;
      } else {
        ToastHelper.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      // ToastHelper.error(
      //   "Failed to connect to the server. Please try again later."
      // );
    } finally {
      // setIsLoading(false);
    }
    return null;
  };

  const getNextSizeId = () => {
    const items = initialData?.items ?? [];
    const lastItem = items[items?.length - 1];
    const lastSizeId = lastItem?.productVariantSize?.length ?? 0;
    return lastSizeId + 1;
  };

  const initialSize = {
    id: 0,
    metric: "",
    metricValue: "",
    mrp: "",
    sellingPrice: "",
    availableQuantity: "",
  };
  const initialValues = {
    color: "",
    media: Array(5).fill({ media: null, altText: "" }),
    productVariantSize: [initialSize],
  };

  const populateDropdowns = async (editData) => {
    try {
      const layer1Options = await fetchCategories();
      setCategoryList1(
        layer1Options?.map((item) => ({ label: item.title, value: item.id }))
      );

      if (editData?.categoryLayer1Id) {
        const layer2Options = await fetchCategories(editData.categoryLayer1Id);
        setCategoryList2(
          layer2Options?.map((item) => ({ label: item.title, value: item.id }))
        );
      }

      if (editData?.categoryLayer2Id) {
        const layer3Options = await fetchCategories(editData.categoryLayer2Id);
        setCategoryList3(
          layer3Options?.map((item) => ({ label: item.title, value: item.id }))
        );
      }
    } catch (error) {
      console.error("Error populating dropdowns", error);
    }
  };

  const prepareFormData = (apiData) => {
    return apiData?.map((item) => ({
      id: item?.id,
      color: item?.colorCode || "",
      media: Array(5)
        ?.fill({ id: null, media: null, altText: "" })
        ?.map((_, index) => {
          const media = item?.productVariantMedia[index] || {};
          return {
            id: media?.id,
            media: media?.mediaUrl
              ? `${IMAGE_BASE_PATH}${media?.mediaUrl}`
              : null,
            altText: media?.mediaAltText || "",
          };
        }),
      productVariantSize: item?.productVariantSize?.map((size) => ({
        id: size?.id || initialSize?.id,
        metric: size?.metric || initialSize?.metric,
        metricValue: size?.metricValue || initialSize?.metricValue,
        mrp: size?.mrp || initialSize?.mrp,
        sellingPrice: size?.sellingPrice || initialSize?.sellingPrice,
        availableQuantity:
          size?.availableQuantity || initialSize?.availableQuantity,
      })),
    }));
  };

  useEffect(() => {
    if (productData && current === 0) {
      form.setFieldsValue({
        title: productData?.title,
        description: productData?.description,
        metaTitle: productData?.metaTitle,
        metaDescription: productData?.metaDescription,
        urlName: productData?.urlName,
        schema: productData?.schema,
        mrp: productData?.mrp,
        sellingPrice: productData?.sellingPrice,
        categoryLayer1Id: productData?.categoryLayer1Id,
        categoryLayer2Id: productData?.categoryLayer2Id,
        categoryLayer3Id: productData?.categoryLayer3Id,
        productWeight: productData?.productWeight,
        countryOfOrigin: productData?.countryOfOrigin,
        moreInfo: productData?.moreInfo,
        gstId: productData?.gstId,
      });

      populateDropdowns(productData);
    } else if (productData && current === 1) {
      getProductVarientData(productData?.id);
    }
  }, [productData, current]);

  const getProductVarientData = async (id) => {
    try {
      const res = await getApi(`${GET_PRODUCT_VARIENT_DATA}?productId=${id}`);
      setProductId(id);
      if (res?.data?.length !== 0) {
        const formData = await prepareFormData(res?.data);
        form.setFieldsValue({ items: formData });
      } else {
        form.setFieldsValue({ items: initialValues });
      }
    } catch (error) {
      console.error("Error fetching categories", error);
    }
  };

  const fetchGsts = async () => {
    try {
      const res = await getApi(GET_GST_DROPDOWN);
      if (res?.data?.length !== 0) {
        const data = res?.map((item) => ({
          label: `${item?.hsnCode} - (${item?.description})`,
          value: item?.gstId,
        }));
        setGstValues(data);
      } else {
        ToastHelper.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.log("Failed to connect to the server. Please try again later.");
    }
  };

  const fetchCategories = async (parentId = 0) => {
    const payload = {
      pageIndex: 0,
      pageSize: 100,
      searchString: "",
      parentCategoryId: parentId,
    };
    try {
      const res = await postApi(`${GET_CATEGORIES_LIST}`, payload);
      return res?.data || [];
    } catch (error) {
      console.error("Error fetching categories", error);
      return [];
    }
  };

  useEffect(() => {
    (async () => {
      const options = await fetchCategories();
      setCategoryList1(
        options?.map((item) => ({ label: item.title, value: item.id }))
      );
    })();
    fetchGsts();
  }, []);

  const handleLayer1Change = async (value) => {
    form.setFieldsValue({ categoryLayer2Id: null, categoryLayer3Id: null });
    const options = await fetchCategories(value);
    setCategoryList2(
      options?.map((item) => ({ label: item.title, value: item.id }))
    );
    setCategoryList3([]);
  };

  const handleLayer2Change = async (value) => {
    form.setFieldsValue({ categoryLayer3Id: null });
    const options = await fetchCategories(value);
    setCategoryList3(
      options?.map((item) => ({ label: item.title, value: item.id }))
    );
  };

  const getColorData = (inputDataColor) => {
    return (
      colorOptions?.find((color) => color?.colorCode === inputDataColor) || null
    );
  };

  const getSizeData = (sizeCode) => {
    const selectedSize = sizeOptions?.find((size) => size?.value === sizeCode);
    return selectedSize;
  };

  const processMedia = (mediaArray) => {
    const filteredMedia = mediaArray?.filter((item) => item?.media !== null);

    const processedMedia = filteredMedia?.map((item) => ({
      ...item,
      media: item?.media?.split(",")[1] ?? null,
    }));

    return processedMedia;
  };

  const transformProductData = (inputData) => {
    return inputData?.map((item) => ({
      id: id ? item?.id : 0,
      productId: productId,
      gstId: inputData?.gstId,
      color: getColorData(item?.color)?.color,
      colorCode: item?.color,
      media: processMedia(item?.media),
      productVariantSize: item?.productVariantSize?.map((size) => {
        return {
          id: size?.id,
          metric: size?.metric,
          metricValue: size?.metricValue,
          mrp: size?.mrp,
          sellingPrice: size?.sellingPrice,
          availableQuantity: size?.availableQuantity,
        };
      }),
    }));
  };

  const next = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const payload = {
        id: id ? id : undefined,
        title: values?.title,
        description: values?.description,
        metaTitle: values?.metaTitle,
        metaDescription: values?.metaDescription,
        urlName: values?.urlName,
        schema: values?.schema,
        mrp: values?.mrp,
        categoryLayer1Id: values?.categoryLayer1Id,
        categoryLayer2Id: values?.categoryLayer2Id,
        categoryLayer3Id: values?.categoryLayer3Id,
        sellingPrice: values?.sellingPrice,
        productWeight: values?.productWeight,
        countryOfOrigin: values?.countryOfOrigin,
        moreInfo: values?.moreInfo,
        gstId: values?.gstId,
      };
      setLoading(true);
      const url = id ? UPDATE_PRODUCT : ADD_PRODUCT;
      const res = await postApi(`${url}`, payload);
      setLoading(false);
      message.success(`Product data ${id ? "updated" : "saved"} successfully`);
      setCurrent(current + 1);
      setProductId(res?.data);
      return res?.data || [];
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const save = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const data = transformProductData(values?.items);
      setLoading(true);
      const res = await postApi(`${ADD_OR_UPDATE_PRODUCT_VARIANT}`, data);
      setLoading(false);
      message.success(
        `Product varient ${id ? "updated" : "saved"} successfully`
      );
      navigate("/manage/products");
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const step1Form = (
    <>
      <div className="form1_category">
        <FormSelect
          label="Category Layer 1"
          name="categoryLayer1Id"
          options={categoryList1}
          placeholder="Select Category Layer 1"
          rules={[{ required: true, message: "Category Layer 1 is required" }]}
          onChange={handleLayer1Change}
        />
        <FormSelect
          label="Category Layer 2"
          name="categoryLayer2Id"
          options={categoryList2}
          placeholder="Select Category Layer 2"
          rules={[{ required: true, message: "Category Layer 2 is required" }]}
          onChange={handleLayer2Change}
        />
        <FormSelect
          label="Category Layer 2"
          name="categoryLayer3Id"
          options={categoryList3}
          placeholder="Select Category Layer 3"
          rules={[{ required: true, message: "Category Layer 3 is required" }]}
        />
      </div>
      <FormSelect
        label="HSN code"
        name="gstId"
        options={gstValues}
        placeholder="Select HSN code"
        rules={[{ required: true, message: "HSN code is required" }]}
      />
      <FormInput
        label="Title"
        name="title"
        rules={[{ required: true, message: "Please enter title" }]}
        placeholder="Enter product title"
      />
      <FormEditor
        label="Description"
        name="description"
        rules={[{ required: true, message: "Please enter description" }]}
        placeholder="Enter description"
      />
      <FormInput
        label="Meta Title"
        name="metaTitle"
        rules={[{ required: true, message: "Please enter meta title" }]}
        placeholder="Enter meta title"
        showCount={true}
      />
      <FormTextarea
        label="Meta Description"
        name="metaDescription"
        rules={[
          {
            required: true,
            message: "Please enter meta description",
          },
        ]}
        placeholder="Enter meta description"
        showCount={true}
        minLength={30}
        maxLength={200}
      />

      <FormInput
        label="URL Name"
        name="urlName"
        rules={[{ required: true, message: "Please enter URL name" }]}
        placeholder="Enter URL name"
      />
      {/* <Form.List name="schema" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} className="schema">
                <FormSelect
                  {...restField}
                  label="Schema Type"
                  name={[name, "type"]}
                  rules={[
                    {
                      required: true,
                      message: "Please select a schema type",
                    },
                  ]}
                  options={schemaTypes}
                  placeholder="Select a schema type"
                />
                <FormTextarea
                  label="Schema Content"
                  name={[name, "content"]}
                  rules={[
                    {
                      required: true,
                      message: "Please enter schema content",
                    },
                  ]}
                  placeholder="Enter schema content"
                />

                <div className="remove-schema-button-container">
                  <Button
                    onClick={() => remove(name)}
                    style={{
                      color: "white",
                      backgroundColor: "red",
                      borderColor: "red",
                    }}
                  >
                    Remove Schema
                  </Button>
                </div>
              </div>
            ))}

            <div className="add-schema-button-container">
              <Button
                type="dashed"
                onClick={() => add()}
                style={{
                  color: "white",
                  backgroundColor: "green",
                  borderColor: "green",
                }}
              >
                Add More Schema
              </Button>
            </div>
          </>
        )}
      </Form.List> */}
      <div className="form1_price">
        <FormNumericInput
          label="MRP"
          name="mrp"
          rules={[{ required: true, message: "Please enter MRP" }]}
          placeholder="Enter MRP"
          form={form}
        />

        <FormNumericInput
          label="Selling price"
          name="sellingPrice"
          rules={[{ required: true, message: "Please enter selling price" }]}
          placeholder="Enter selling price"
          form={form}
        />

        <FormWeight
          label="Product Weight (grams)"
          name="productWeight"
          rules={[
            { required: true, message: "Please enter the product weight" },
          ]}
          type="number"
          placeholder="Enter product weight in grams"
          addonAfter="grams"
        />

        <FormInput
          label="Country of Origin"
          name="countryOfOrigin"
          rules={[
            { required: true, message: "Please enter country of origin" },
          ]}
          placeholder="Enter country of origin"
        />
      </div>

      <FormEditor
        label="More Information"
        name="moreInfo"
        rules={[{ required: true, message: "Please enter more info" }]}
        placeholder="Enter more information"
      />
    </>
  );

  const step2Form = (
    <Form.List name="items" initialValue={[initialValues]}>
      {(fields, { add, remove }) => (
        <>
          {fields?.map(({ key, name, ...restField }, colorIndex) => (
            <div key={key} className="step2form">
              <FormSelectColor
                label="Color"
                name={[name, "color"]}
                options={colorOptions?.map((option) => ({
                  label: option.color,
                  value: option.colorCode,
                }))}
                placeholder="Select a color"
                rules={[{ required: true, message: "Please select a color" }]}
                style={{
                  maxWidth: 300,
                }}
              />

              <div className="media-upload">
                <Form.List name={[name, "media"]}>
                  {(imageFields) => (
                    <>
                      {imageFields?.map(
                        ({ key: imageKey, name: imageName }, imageIndex) => {
                          const mediaData =
                            productData && initialData?.items
                              ? initialData?.items[colorIndex]?.media[
                                  imageIndex
                                ]?.media || null
                              : null;
                          return (
                            <div key={imageKey} className="media-upload_card">
                              <ProductUploadWithCrop
                                form={form}
                                image={mediaData}
                                name={[name, "media", imageName]}
                              />
                            </div>
                          );
                        }
                      )}
                    </>
                  )}
                </Form.List>
              </div>

              <Form.List name={[name, "productVariantSize"]}>
                {(
                  sizeFields,
                  { add: addSizeField, remove: removeSizeField }
                ) => (
                  <>
                    {sizeFields?.map(
                      (
                        { key: sizeKey, name: sizeName, ...sizeRestField },
                        sizeIndex
                      ) => (
                        <div key={sizeKey} className="size-section">
                          <div className="size-fields">
                            <FormInput
                              label="Display Size"
                              name={[sizeName, "metric"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter Display size",
                                },
                              ]}
                              placeholder="Enter display size"
                            />

                            <FormNumericInput
                              label="Size value"
                              name={[sizeName, "metricValue"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter size value",
                                },
                              ]}
                              placeholder="Enter size value"
                              form={form}
                            />

                            <FormNumericInput
                              {...sizeRestField}
                              label="MRP"
                              name={[sizeName, "mrp"]}
                              rules={[
                                { required: true, message: "Please enter MRP" },
                              ]}
                              placeholder="Enter MRP"
                              form={form}
                            />
                            <FormNumericInput
                              {...sizeRestField}
                              label="Selling Price"
                              name={[sizeName, "sellingPrice"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter selling price",
                                },
                              ]}
                              placeholder="Enter selling price"
                              form={form}
                            />
                            <FormNumericInput
                              {...sizeRestField}
                              label="Available Quantity"
                              name={[sizeName, "availableQuantity"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter available quantity",
                                },
                              ]}
                              placeholder="Enter available quantity"
                              form={form}
                            />
                          </div>
                          <div className="add-products_size_buttons">
                            {sizeFields?.length - 1 === sizeIndex && (
                              <Button
                                type="dashed"
                                style={{
                                  color: "white",
                                  backgroundColor: "green",
                                  borderColor: "green",
                                }}
                                onClick={() => addSizeField(initialSize)}
                              >
                                Add More Size
                              </Button>
                            )}
                            {sizeIndex !== 0 && (
                              <Button
                                style={{
                                  color: "white",
                                  backgroundColor: "red",
                                  borderColor: "red",
                                }}
                                onClick={() => removeSizeField(sizeName)}
                              >
                                Remove Size
                              </Button>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </>
                )}
              </Form.List>

              {fields.length > 1 && (
                <Button
                  type="dashed"
                  style={{
                    color: "white",
                    backgroundColor: "red",
                    borderColor: "red",
                    margin: "10px 0",
                  }}
                  onClick={() => remove(name)}
                >
                  Remove Color
                </Button>
              )}
            </div>
          ))}
          <Button
            type="dashed"
            style={{ marginTop: "20px" }}
            onClick={() => add(initialValues)}
          >
            Add More Color
          </Button>
        </>
      )}
    </Form.List>
  );

  return (
    <div className="add-products">
      <div className="add-products_heading">
        <h1>{id ? "Edit" : "Add"} Product</h1>
      </div>
      <div className="add-products_container">
        <Steps current={current}>
          <Step title="Basic Information" />
          <Step title="Additional Information" />
        </Steps>

        <Form form={form} layout="vertical">
          <div className="form-grid">
            {current === 0 && step1Form}
            {current === 1 && step2Form}
          </div>

          <div className="form-submit">
            {current === 1 && (
              <Button
                style={{ marginRight: "8px" }}
                onClick={() => setCurrent(current - 1)}
              >
                Back
              </Button>
            )}
            <Button
              loading={loading}
              type="primary"
              onClick={current === 1 ? save : next}
            >
              {current === 1 ? "Save" : "Save & Continue"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddProducts;
